import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import { getTicketsForDailyReport } from '../../../api/tickets';
import { getWorkItemsForDailyReport } from '../../../api/workItems';
import { compareWorkItemSiblings } from '../../../constants/workItem';
import { getTodayUnassignedTicketIds, getTodayUnassignedWorkItemIds } from '../../../api/auditLog';
import { getDeliverableItemsForUser } from '../../../api/deliverables';
import { getDailyReportVisibleMembers } from '../../../api/departments';
import { getProjectProfile, updateUserSettings } from '../../../api/profiles';
import { useToast } from '../../common/Toast';
import MarkdownViewer from '../../common/MarkdownViewer';
import Avatar from '../../common/Avatar';
import MemberVisibilityModal from './MemberVisibilityModal';
import BetaDailyReportProjectSelector from './BetaDailyReportProjectSelector';
import CustomSelect from '../../common/CustomSelect';
import { useDailyReportBroadcast } from '../../../hooks/useDailyReportBroadcast';
import './BetaDailyReportView.scss';

// 未分組的 key,對齊工作項目列表的慣例
const UNGROUPED_KEY = '__ungrouped__';

// 群組排序:未分組一律最後。日報用 DB 的 sort_order(全域),不用工作項目列表的個人排序 ——
// 否則同一份報告每個人看到的群組順序不同,報告時會對不上。
//
// 注意 sort_order 目前實際上全部是 0(從沒被寫過,列表的群組排序存在個人設定裡),
// 所以真正在決定順序的是名稱那一段。沒有這個 tiebreaker 群組順序就是不確定的。
const groupKeyOf = (w) => (w.group?.id ?? UNGROUPED_KEY);
const groupNameOf = (w) => (w.group?.name || '未分組');
const compareByGroup = (a, b) => {
  const ra = a.group ? (a.group.sort_order ?? 0) : Number.MAX_SAFE_INTEGER;
  const rb = b.group ? (b.group.sort_order ?? 0) : Number.MAX_SAFE_INTEGER;
  if (ra !== rb) return ra - rb;
  if (!a.group || !b.group) return 0; // 都是未分組
  return String(a.group.name || '').localeCompare(String(b.group.name || ''), 'zh-Hant', { numeric: true });
};

const BetaDailyReportView = ({ onClose, onNavigate }) => {
  const toast = useToast();
  const {
    profile,
    viewingCompanyId,
    isSuperAdmin,
    isCompanyAdmin,
    isDepartmentAdmin,
  } = useAuth();
  const { companyUsers, usersMapByEmail, statusOptions, departments } = useBetaData();

  // 部門選擇器（公司管理員+）
  const [selectedDepartmentId, setSelectedDepartmentId] = useState(null);

  // 成員
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState(null);

  // 資料
  const [memberProjects, setMemberProjects] = useState([]);
  const [memberDeliverableItems, setMemberDeliverableItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [showMemberVisibility, setShowMemberVisibility] = useState(false);

  // 展開/收合
  const [expandedTickets, setExpandedTickets] = useState(new Set());
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const [hiddenTicketIds, setHiddenTicketIds] = useState(new Set());
  const [showHiddenList, setShowHiddenList] = useState(false);

  // 簡報模式
  const [showBroadcastPanel, setShowBroadcastPanel] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const savedPrepRef = useRef(null);
  const skipNextMemberLoadRef = useRef(false);
  const prevIsPresenterRef = useRef(false);
  const broadcastTimerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const scrollThrottleRef = useRef(null);
  const isHostRef = useRef(false);
  const participantsRef = useRef([]);

  // 保存當前準備狀態（加入 session 時 / 離開 Presenter 角色時）
  const saveMyState = useCallback(() => {
    savedPrepRef.current = {
      selectedMember,
      activeTab,
      expandedTickets: new Set(expandedTickets),
      expandedProjects: new Set(expandedProjects),
      hiddenTicketIds: new Set(hiddenTicketIds),
      memberProjects: [...memberProjects],
      memberDeliverableItems: [...memberDeliverableItems],
    };
  });

  // 恢復準備狀態（變成 Presenter 時 / session 結束時）
  const restoreMyState = useCallback(() => {
    if (!savedPrepRef.current) return;
    const saved = savedPrepRef.current;
    // 如果 savedMember 有值，跳過下一次 loadMemberData（資料已從 saved 恢復）
    if (saved.selectedMember) {
      skipNextMemberLoadRef.current = true;
      // 如果 selectedMember 沒變，useEffect 不會觸發來消耗 flag，用 setTimeout 清除
      setTimeout(() => { skipNextMemberLoadRef.current = false; }, 0);
    }
    setSelectedMember(saved.selectedMember);
    setActiveTab(saved.activeTab);
    setExpandedTickets(saved.expandedTickets);
    setExpandedProjects(saved.expandedProjects);
    setHiddenTicketIds(saved.hiddenTicketIds);
    setMemberProjects(saved.memberProjects);
    setMemberDeliverableItems(saved.memberDeliverableItems);
  }, []);

  // 收到 Presenter UI 狀態廣播 — 只有 Host 套用（Viewer 保持自己的狀態）
  const handleBroadcastStateReceived = useCallback((state) => {
    if (!isHostRef.current) return;
    if (state.selectedMember !== undefined) setSelectedMember(state.selectedMember);
    if (state.activeTab !== undefined) setActiveTab(state.activeTab);
    if (state.expandedTickets) setExpandedTickets(new Set(state.expandedTickets));
    if (state.expandedProjects) setExpandedProjects(new Set(state.expandedProjects));
    if (state.hiddenTicketIds) setHiddenTicketIds(new Set(state.hiddenTicketIds));
    if (state.scrollRatio !== undefined && scrollContainerRef.current) {
      const el = scrollContainerRef.current;
      el.scrollTop = state.scrollRatio * (el.scrollHeight - el.clientHeight);
    }
  }, []);

  // 收到 Presenter 資料廣播 — 只有 Host 套用
  const handleBroadcastDataReceived = useCallback((data) => {
    if (!isHostRef.current) return;
    if (data.memberProjects) setMemberProjects(data.memberProjects);
    if (data.memberDeliverableItems !== undefined) setMemberDeliverableItems(data.memberDeliverableItems);
  }, []);

  // Session 結束 → Host 恢復自己的狀態（非 Host 保持當前狀態）
  const handleSessionEnd = useCallback(() => {
    if (isHostRef.current) restoreMyState();
    savedPrepRef.current = null;
    setShowBroadcastPanel(false);
  }, [restoreMyState]);

  // 指定的 presenter 沒在時間內 ack（對方可能斷線、ghost ref、或訊息路由失敗）
  // hook 已自動把 presenter 還給 Host，這裡只負責提示
  const handlePresenterAckMissed = useCallback((targetUserId) => {
    const target = participantsRef.current.find(p => p.id === targetUserId);
    const name = target?.name || '對方';
    toast.error(`${name} 未回應指定，可能已離線。請對方重新整理頁面後再試`);
  }, [toast]);

  // 訂閱 channel 失敗（CHANNEL_ERROR / TIMED_OUT / CLOSED）
  // hook 已自動 cleanup，這裡跳 toast 讓使用者知道可以重試
  const handleSubscribeError = useCallback(() => {
    toast.error('加入失敗，請稍候再試（若持續失敗請關閉視窗重開）');
  }, [toast]);

  const {
    isInSession,
    isHost,
    isPresenter,
    sessionCode,
    participants,
    presenterId,
    createSession,
    joinSession,
    endSession,
    leaveSession,
    assignPresenter,
    broadcastState,
    broadcastData,
  } = useDailyReportBroadcast({
    profile,
    onStateReceived: handleBroadcastStateReceived,
    onDataReceived: handleBroadcastDataReceived,
    onSessionEnd: handleSessionEnd,
    onPresenterAckMissed: handlePresenterAckMissed,
    onSubscribeError: handleSubscribeError,
  });

  // 保持 isHostRef 同步（handleSessionEnd 需要透過 ref 讀取，避免循環依賴）
  useEffect(() => { isHostRef.current = isHost; }, [isHost]);

  // 保持 participantsRef 同步（handlePresenterAckMissed 需要查名字，但 handler 在
  // useDailyReportBroadcast destructure 之前定義，閉包拿不到 participants）
  useEffect(() => { participantsRef.current = participants; }, [participants]);

  // 加入 session 時保存、離開 session 時恢復
  const handleCreateSession = useCallback((code) => {
    saveMyState();
    createSession(code);
  }, [createSession, saveMyState]);

  const handleJoinSession = useCallback((code) => {
    saveMyState();
    joinSession(code);
  }, [joinSession, saveMyState]);

  const handleLeaveSession = useCallback(() => {
    if (isHost) restoreMyState();
    savedPrepRef.current = null;
    leaveSession();
  }, [leaveSession, restoreMyState, isHost]);

  const handleEndSession = useCallback(() => {
    endSession();
    // endSession 觸發 handleSessionEnd → restoreMyState
  }, [endSession]);

  // Presenter 角色切換（只有 Host 需要 save/restore，因為只有 Host 的狀態會被廣播覆蓋）
  useEffect(() => {
    if (!isInSession) {
      prevIsPresenterRef.current = false;
      return;
    }
    const wasPresenter = prevIsPresenterRef.current;
    if (isPresenter && !wasPresenter) {
      // 剛變成 Presenter → Host 恢復自己的狀態（非 Host 的當前狀態就是準備好的）
      if (isHost) restoreMyState();
    } else if (!isPresenter && wasPresenter) {
      // 剛離開 Presenter → Host 保存狀態（即將被新 Presenter 廣播覆蓋）
      if (isHost) saveMyState();
    }
    prevIsPresenterRef.current = isPresenter;
  }, [isPresenter, isInSession, isHost]);

  // Presenter：資料變化時廣播 data_sync（切換成員、重整時才觸發）
  useEffect(() => {
    if (!isPresenter || !isInSession) return;
    broadcastData({ memberProjects, memberDeliverableItems });
  }, [memberProjects, memberDeliverableItems, isPresenter, isInSession]);

  // Presenter：UI 狀態變化時廣播 state_sync（輕量，頻繁）
  useEffect(() => {
    if (!isPresenter || !isInSession) return;
    if (broadcastTimerRef.current) clearTimeout(broadcastTimerRef.current);
    broadcastTimerRef.current = setTimeout(() => {
      broadcastState({
        selectedMember,
        activeTab,
        expandedTickets: [...expandedTickets],
        expandedProjects: [...expandedProjects],
        hiddenTicketIds: [...hiddenTicketIds],
      });
    }, 50);
  }, [selectedMember, activeTab, expandedTickets, expandedProjects, hiddenTicketIds, isPresenter, isInSession]);

  // Presenter 捲動時同步捲動比例（throttle 100ms）
  const handlePresenterScroll = useCallback((e) => {
    if (!isPresenter || !isInSession) return;
    if (scrollThrottleRef.current) return;
    scrollThrottleRef.current = setTimeout(() => {
      scrollThrottleRef.current = null;
      const el = e.target;
      const maxScroll = el.scrollHeight - el.clientHeight;
      broadcastState({ scrollRatio: maxScroll > 0 ? el.scrollTop / maxScroll : 0 });
    }, 100);
  }, [isPresenter, isInSession, broadcastState]);

  // 新參與者加入時，Presenter 重新廣播完整狀態（state + data）
  useEffect(() => {
    if (!isPresenter || !isInSession || participants.length === 0) return;
    broadcastState({
      selectedMember,
      activeTab,
      expandedTickets: [...expandedTickets],
      expandedProjects: [...expandedProjects],
      hiddenTicketIds: [...hiddenTicketIds],
    });
    broadcastData({ memberProjects, memberDeliverableItems });
  }, [participants.length]);

  // 重複 Host 偵測：如果已有更早加入的 Host，自己退出
  useEffect(() => {
    if (!isHost || !isInSession || participants.length <= 1) return;
    const hosts = participants.filter(p => p.isHost);
    if (hosts.length <= 1) return;
    const me = hosts.find(p => p.id === profile?.id);
    const otherHost = hosts.find(p => p.id !== profile?.id);
    if (me && otherHost && otherHost.joinedAt < me.joinedAt) {
      toast.error(`${otherHost.name} 已在主持中`);
      leaveSession();
    }
  }, [participants, isHost, isInSession]);


  // 搜尋高亮
  const [highlightKeyword, setHighlightKeyword] = useState('');
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const globalMatchCounterRef = useRef(0);

  // 預定義高亮顏色
  const PRESET_HIGHLIGHT_COLORS = [
    { background: '#ffeb3b', color: '#000' },
    { background: '#80cbc4', color: '#000' },
    { background: '#ce93d8', color: '#000' },
    { background: '#ffcc80', color: '#000' },
    { background: '#a5d6a7', color: '#000' },
  ];

  const escapeRegExp = (string) => string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const generateColorFromHash = (text) => {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      hash = text.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs(hash % 360);
    const saturation = 65 + (Math.abs(hash) % 20);
    const lightness = 85 + (Math.abs(hash >> 8) % 10);
    return { background: `hsl(${hue}, ${saturation}%, ${lightness}%)`, color: '#000' };
  };

  const getHighlightStyle = (keywordIndex, keyword) => {
    if (keywordIndex < PRESET_HIGHLIGHT_COLORS.length) {
      return PRESET_HIGHLIGHT_COLORS[keywordIndex];
    }
    return generateColorFromHash(keyword);
  };

  const highlightText = (text, searchText) => {
    if (!searchText || !text) return text;
    const keywords = searchText.trim().split(/\s+/).filter(k => k.length > 0);
    if (keywords.length === 0) return text;

    const escapedKeywords = keywords.map(k => escapeRegExp(k));
    const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'gi');
    const parts = text.split(pattern);

    return (
      <>
        {parts.map((part, index) => {
          const keywordIndex = keywords.findIndex(k => k.toLowerCase() === part.toLowerCase());
          if (keywordIndex !== -1) {
            const style = getHighlightStyle(keywordIndex, keywords[keywordIndex]);
            const globalMatchIndex = globalMatchCounterRef.current++;
            const isCurrentMatch = globalMatchIndex === currentMatchIndex;
            return (
              <mark
                key={index}
                className={`highlight ${isCurrentMatch ? 'current-match' : ''}`}
                data-match-index={globalMatchIndex}
                style={{
                  backgroundColor: isCurrentMatch ? '#ff6b35' : style.background,
                  color: isCurrentMatch ? '#fff' : style.color,
                  padding: '2px 4px',
                  borderRadius: '2px',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  boxShadow: isCurrentMatch
                    ? '0 0 0 3px rgba(255, 107, 53, 0.3), 0 0 12px rgba(255, 107, 53, 0.6)'
                    : 'none',
                  outline: isCurrentMatch ? '2px solid #ff6b35' : 'none',
                  outlineOffset: '2px',
                }}
              >
                {part}
              </mark>
            );
          }
          return <span key={index}>{part}</span>;
        })}
      </>
    );
  };

  const countHighlightMatches = () => {
    if (!highlightKeyword || memberProjects.length === 0) return 0;
    const keywords = highlightKeyword.trim().split(/\s+/).filter(k => k.length > 0);
    if (keywords.length === 0) return 0;

    let count = 0;
    memberProjects.forEach(project => {
      keywords.forEach(keyword => {
        const regex = new RegExp(escapeRegExp(keyword), 'gi');
        const nameMatches = (project.name || '').match(regex);
        if (nameMatches) count += nameMatches.length;
        const descMatches = (project.description || '').match(regex);
        if (descMatches) count += descMatches.length;
      });
      project.tickets.forEach(ticket => {
        keywords.forEach(keyword => {
          const regex = new RegExp(escapeRegExp(keyword), 'gi');
          const titleMatches = (ticket.title || '').match(regex);
          if (titleMatches) count += titleMatches.length;
        });
      });
    });
    return count;
  };

  useEffect(() => {
    const count = countHighlightMatches();
    setTotalMatches(count);
    setCurrentMatchIndex(0);
  }, [highlightKeyword, memberProjects]);

  const scrollToMatch = (matchIndex) => {
    if (totalMatches === 0) return;
    const allHighlights = document.querySelectorAll('.highlight[data-match-index]');
    const target = allHighlights[matchIndex];
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter' && totalMatches > 0) {
      e.preventDefault();
      let nextIndex;
      if (e.shiftKey) {
        nextIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : totalMatches - 1;
      } else {
        nextIndex = currentMatchIndex < totalMatches - 1 ? currentMatchIndex + 1 : 0;
      }
      setCurrentMatchIndex(nextIndex);
      setTimeout(() => scrollToMatch(nextIndex), 100);
    }
  };

  const goToPreviousMatch = () => {
    if (totalMatches === 0) return;
    const prevIndex = currentMatchIndex > 0 ? currentMatchIndex - 1 : totalMatches - 1;
    setCurrentMatchIndex(prevIndex);
    setTimeout(() => scrollToMatch(prevIndex), 100);
  };

  const goToNextMatch = () => {
    if (totalMatches === 0) return;
    const nextIndex = currentMatchIndex < totalMatches - 1 ? currentMatchIndex + 1 : 0;
    setCurrentMatchIndex(nextIndex);
    setTimeout(() => scrollToMatch(nextIndex), 100);
  };

  // === 取得有效的部門 ID ===
  // admin+：用 DailyReport 本地的部門選擇器
  // 一般成員/部門管理員：直接用自己的 profile.department_id
  const effectiveDepartmentId = useMemo(() => {
    if (isCompanyAdmin() || isSuperAdmin()) {
      return selectedDepartmentId;
    }
    return profile?.department_id;
  }, [selectedDepartmentId, profile?.department_id]);

  // === 部門列表來自 BetaDataContext，自動設定預設部門（記住上次選擇）===
  useEffect(() => {
    if ((isCompanyAdmin() || isSuperAdmin()) && departments.length > 0 && !selectedDepartmentId) {
      // 優先讀取上次儲存的 DailyReport 檢視部門
      const savedDeptId = profile?.settings?.daily_report_viewing_department_id;
      const savedDept = savedDeptId && departments.find(d => d.id === savedDeptId);
      const defaultDept = savedDept || departments.find(d => d.id === profile?.department_id) || departments[0];
      setSelectedDepartmentId(defaultDept.id);
    }
  }, [departments]);

  // === 部門切換時儲存到 profile.settings（super_admin / company_admin）===
  const saveDepartmentSelection = useCallback(async (deptId) => {
    if (!profile?.id || (!isSuperAdmin() && !isCompanyAdmin())) return;
    const newSettings = {
      ...(profile?.settings || {}),
      daily_report_viewing_department_id: deptId,
    };
    await updateUserSettings(profile.id, newSettings);
  }, [profile?.id, profile?.settings]);

  // 固定部門代碼（從 department ID 產生，每次都一樣）
  const departmentCode = useMemo(() => {
    const deptId = effectiveDepartmentId;
    if (!deptId) return '';
    let hash = 0;
    for (let i = 0; i < deptId.length; i++) {
      hash = deptId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars[Math.abs((hash >> (i * 4)) % chars.length)];
    }
    return code;
  }, [effectiveDepartmentId]);

  // 部門代碼變更時自動更新 joinCode（super_admin 切換檢視部門時）
  useEffect(() => {
    if (departmentCode && !isInSession) {
      setJoinCode(departmentCode);
    }
  }, [departmentCode]);

  // === 載入成員（部門變更時）===
  useEffect(() => {
    if (effectiveDepartmentId) {
      loadMembers(effectiveDepartmentId);
      setSelectedMember(null);
      setMemberProjects([]);
    }
  }, [effectiveDepartmentId]);

  const loadMembers = async (departmentId) => {
    setMembersLoading(true);
    const { data: visibleMemberIds, error } = await getDailyReportVisibleMembers(departmentId);

    if (error) {
      console.error('載入顯示設定失敗:', error);
    }

    if (!visibleMemberIds || visibleMemberIds.length === 0) {
      setMembers([]);
      setMembersLoading(false);
      return;
    }

    // 用 companyUsers 做 client-side join，按 visibleMemberIds 順序排列
    const visibleMembers = visibleMemberIds
      .map(id => companyUsers.find(u => u.id === id))
      .filter(m => m);

    setMembers(visibleMembers);
    setMembersLoading(false);
  };

  // === 載入成員的 tickets ===
  useEffect(() => {
    if (skipNextMemberLoadRef.current) {
      skipNextMemberLoadRef.current = false;
      return;
    }
    // Host 非 Presenter 時：資料從廣播來，不自己 load
    if (isInSession && isHost && !isPresenter) return;
    if (selectedMember) {
      loadMemberData(selectedMember);
    } else {
      setMemberProjects([]);
    }
  }, [selectedMember]);

  const loadMemberData = async (memberId) => {
    setLoading(true);
    try {
      // 1. 並行：今日 unassign(ticket + work_item) + 專案顯示設定 + 交付項目(引退中,暫留撈取)
      const [
        { data: unassignedIds }, { data: unassignedWiIds },
        { data: projectProfile }, { data: deliverableItems },
      ] = await Promise.all([
        getTodayUnassignedTicketIds(memberId),
        getTodayUnassignedWorkItemIds(memberId),
        getProjectProfile(memberId),
        getDeliverableItemsForUser(memberId, { companyId: viewingCompanyId }),
      ]);
      const visibleProjectIds = projectProfile?.settings?.daily_report_visible_project_ids;
      setMemberDeliverableItems(deliverableItems || []);

      // 2. 查詢 tickets + work_items（各自依賴自己的 unassignedIds）
      const [{ data: tickets, error: tErr }, { data: workItems, error: wErr }] = await Promise.all([
        getTicketsForDailyReport(memberId, {
          companyId: viewingCompanyId,
          todayUnassignedTicketIds: unassignedIds || [],
        }),
        getWorkItemsForDailyReport(memberId, {
          companyId: viewingCompanyId,
          todayUnassignedWorkItemIds: unassignedWiIds || [],
        }),
      ]);
      if (tErr) throw tErr;
      if (wErr) throw wErr;

      // 4. 按專案分群（ticket + work_item 混在同一專案）
      const projectMap = {};
      const ensureProject = (project) => {
        if (!projectMap[project.id]) {
          projectMap[project.id] = {
            id: project.id,
            name: project.name,
            color: project.color,
            icon: project.icon,
            description: project.description,
            externalUrl: project.external_url,
            tickets: [],
            workItems: [],
          };
        }
        return projectMap[project.id];
      };
      (tickets || []).forEach(ticket => {
        ensureProject(ticket.projects || { id: ticket.project_id, name: '未分類' }).tickets.push(ticket);
      });
      // work_item 建父子結構:子項掛到母項底下,不要跟母項平排(平排會讓子項夾在
      // 別的母項中間,而且看不出屬於誰)。母項沒指派給我時 API 會補 _context 母項。
      const wiById = new Map((workItems || []).map(w => [w.id, { ...w, children: [] }]));
      wiById.forEach(node => {
        if (node.parent_id && wiById.has(node.parent_id)) wiById.get(node.parent_id).children.push(node);
      });
      wiById.forEach(node => {
        if (node.children.length > 1) node.children.sort(compareWorkItemSiblings);
      });
      wiById.forEach(node => {
        if (node.parent_id && wiById.has(node.parent_id)) return; // 子項由母項帶出
        ensureProject(node.projects || { id: node.project_id, name: '未分類' }).workItems.push(node);
      });
      // 同群組的工作項目聚在一起(日報要按群組唸)。群組順序用 DB 的 sort_order,
      // 不用工作項目列表的個人排序 —— 否則同一份報告每個人看到的順序不同,報告時對不上。
      // 未分組一律排最後。sort 是穩定排序,所以群組內維持原本的編號遞減。
      Object.values(projectMap).forEach(p => {
        p.workItems.sort(compareByGroup);
      });

      // 5. 依使用者設定排序/過濾
      // visibleProjectIds 存在（含空陣列）= 使用者有設定過，尊重選擇
      // visibleProjectIds 不存在（undefined/null）= 從未設定，顯示全部
      let result;
      if (Array.isArray(visibleProjectIds)) {
        result = visibleProjectIds
          .map(id => projectMap[id])
          .filter(Boolean);
      } else {
        result = Object.values(projectMap);
      }

      setMemberProjects(result);
      setExpandedProjects(new Set(result.map(p => p.id)));
      setActiveTab(result.length > 0 ? 'all' : ((deliverableItems || []).length > 0 && showDeliverables ? 'deliverables' : 'all'));
      setHiddenTicketIds(new Set());
      setShowHiddenList(false);
    } catch (error) {
      console.error('載入成員資料失敗:', error);
      toast.error('載入失敗');
    }
    setLoading(false);
  };

  // === 關閉 DailyReport 時處理 session ===
  const handleClose = useCallback(() => {
    if (isInSession) {
      if (isHost) {
        endSession(); // Host 關閉 → 結束所有人的 session
      } else {
        leaveSession(); // 非 Host → 自己離開
      }
    }
    onClose();
  }, [isInSession, isHost, endSession, leaveSession, onClose]);

  // === 展開/收合 ===
  const toggleTicketExpand = (ticketId) => {
    setExpandedTickets(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  const toggleProjectExpand = (projectId) => {
    setExpandedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  // === 隱藏 Ticket ===
  const toggleTicketHidden = (ticketId) => {
    setHiddenTicketIds(prev => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else next.add(ticketId);
      return next;
    });
  };

  // === Ticket 編輯 ===
  const handleEditTicket = (ticket) => {
    onNavigate('ticket', null, ticket.id, { ticketNo: ticket.ticket_no });
  };

  // === work_item 編輯 ===(切到新版 UI 並用 ?wi= 開 drawer,由 BetaLayout handleNavigate 處理)
  // 子項不走完整內頁:開母項 drawer 並帶子項 id,由 WorkItemApp 彈出該子項的輕量 card
  const handleEditWorkItem = (wi) => {
    if (wi.parent_id) {
      onNavigate('work_item', null, wi.parent_id, { subId: wi.id });
    } else {
      onNavigate('work_item', null, wi.id);
    }
  };

  // === 重新整理 ===
  const handleRefresh = async () => {
    if (!selectedMember) return;
    await loadMemberData(selectedMember);
  };

  // 只鎖 Host（Host 的畫面會被 Presenter 廣播覆蓋，其他 Viewer 可自由操作）
  const isLocked = isInSession && isHost && !isPresenter;


  // === Status helpers ===
  const getStatusLabel = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt?.label || status || '待處理';
  };

  const getStatusColor = (status) => {
    const opt = statusOptions.find(o => o.value === status);
    return opt?.color || '#999';
  };

  // === Owner 解析 ===
  // ticket_assignees 已依 assigned_at 升冪排序，[0] 即當前 Owner（與詳情頁一致）
  const resolveTicketOwner = (ticket) => {
    const ownerAssignee = ticket.ticket_assignees?.[0];
    if (!ownerAssignee) return null;
    const email = ownerAssignee.profiles?.email?.toLowerCase();
    const user = (email && usersMapByEmail[email])
      || companyUsers.find(u => u.id === ownerAssignee.user_id)
      || null;
    return {
      avatarUrl: user?.avatar_url,
      name: user?.name || ownerAssignee.profiles?.email || '未知',
    };
  };

  // 預估 / 實際工時。母項通常不填(時數在子項上),沒有就不顯示這個 chip。
  // 格式對齊交付項目區塊的 di-hours。
  const hoursLabel = (wi) => {
    const e = Number(wi.estimated_hours) || 0;
    const a = Number(wi.actual_hours) || 0;
    if (!e && !a) return null;
    return `預估 ${fmtHours(e)}h / 實際 ${fmtHours(a)}h`;
  };

  // 單一工作項目(母項)的列。抽成函式讓群組分隔列可以插在它們之間。
  const renderWorkItemRow = (wi) => {
    const owner = resolveWorkItemOwner(wi);
    const wiKey = `wi-${wi.id}`;
    // _context = 母項沒指派給我,只是拿來當子項的脈絡標題:
    // 不給編輯/隱藏、不算進統計,狀態與時數也不顯示(那不是我的工作)
    const isCtx = !!wi._context;
    return (
    <div
      key={wiKey}
      className={`task-item ${expandedTickets.has(wiKey) ? 'expanded' : ''}${isCtx ? ' context-only' : ''}`}
    >
      <div className="task-header" onClick={() => toggleTicketExpand(wiKey)}>
        <span className="expand-icon">
          {wi.description ? (expandedTickets.has(wiKey) ? '▼' : '▶') : ''}
        </span>
        <div className="task-main">
          <div
            className="task-status-indicator"
            style={{ background: getStatusColor(wi.status) }}
          />
          <div className="task-info">
            <h5 className="task-title">
              <span className="ticket-no-badge wi-badge">
                工作項目{wi.work_item_no ? ` #${wi.work_item_no}` : ''}
              </span>
              {highlightText(wi.title, highlightKeyword)}
              {isCtx && <span className="wi-context-tag" title="未指派給你,僅作為子項的母項脈絡">脈絡</span>}
            </h5>
          </div>
        </div>
        <div className="task-meta">
          {!isCtx && owner && (
            <span className="task-owner" title={`Owner：${owner.name}`}>
              <Avatar avatarUrl={owner.avatarUrl} name={owner.name} size="xsmall" />
              <span className="task-owner-name">{owner.name}</span>
            </span>
          )}
          {!isCtx && (
            <span
              className="task-status"
              style={{ borderColor: getStatusColor(wi.status), color: getStatusColor(wi.status) }}
            >
              {getStatusLabel(wi.status)}
            </span>
          )}
          {/* 有子項 → 顯示子項時數總和(時數通常填在子項上,母項自己多半沒填);
              沒子項才顯示自身。脈絡母項不是我的工作,但底下子項是,所以總和照顯示。*/}
          {(() => {
            // 有子項 → 算子項總和(時數通常填在子項上,母項自己多半沒填);
            // 沒子項 → 用自己。文案與專案標題列一致。
            const src = (wi.children || []).length > 0 ? wi.children : (isCtx ? [] : [wi]);
            const label = hoursSummary(src);
            return label ? <span className="task-hours">{label}</span> : null;
          })()}
          {!isCtx && wi.start_date && wi.end_date && (
            <span className="task-dates">{wi.start_date} ~ {wi.end_date}</span>
          )}
        </div>
        {!isLocked && !isCtx && (
          <>
            <button
              className="edit-task-btn"
              onClick={(e) => { e.stopPropagation(); handleEditWorkItem(wi); }}
              title="編輯工作項目"
            >
              ✏️
            </button>
            <button
              className="hide-task-btn"
              onClick={(e) => { e.stopPropagation(); toggleTicketHidden(wiKey); }}
              title="從報告中隱藏此工作項目"
            >
              隱藏
            </button>
          </>
        )}
      </div>
      {expandedTickets.has(wiKey) && wi.description && (
        <div className="task-details">
          <div className="task-description-full">
            <div className="description-label">工作項目說明：</div>
            <div className="description-content">
              <MarkdownViewer content={wi.description} linkTickets={false} />
            </div>
          </div>
        </div>
      )}
      {/* 子項:收在母項底下的窄列。層級較低所以行高與字級都縮一階,
          但展開說明 / 狀態 / 編輯 / 隱藏一律保留。
          順序由 compareWorkItemSiblings 決定,跟 Drawer 的子項 tab 一致。*/}
      {(wi.children || []).length > 0 && (
        <div className="subtask-list">
          {wi.children.map(sub => {
            const subKey = `wi-${sub.id}`;
            const subOwner = resolveWorkItemOwner(sub);
            return (
              <div key={subKey} className={`subtask-item ${expandedTickets.has(subKey) ? 'expanded' : ''}`}>
                <div className="subtask-header" onClick={() => toggleTicketExpand(subKey)}>
                  <span className="subtask-connector" />
                  <span className="expand-icon">
                    {sub.description ? (expandedTickets.has(subKey) ? '▼' : '▶') : ''}
                  </span>
                  <span
                    className="subtask-status-indicator"
                    style={{ background: getStatusColor(sub.status) }}
                  />
                  <span className="subtask-title">
                    {highlightText(sub.title, highlightKeyword)}
                  </span>
                  <div className="subtask-meta">
                    {subOwner && (
                      <span className="task-owner" title={`Owner：${subOwner.name}`}>
                        <Avatar avatarUrl={subOwner.avatarUrl} name={subOwner.name} size="xsmall" />
                      </span>
                    )}
                    <span
                      className="task-status"
                      style={{ borderColor: getStatusColor(sub.status), color: getStatusColor(sub.status) }}
                    >
                      {getStatusLabel(sub.status)}
                    </span>
                    {hoursLabel(sub) && <span className="task-hours">{hoursLabel(sub)}</span>}
                    {sub.start_date && sub.end_date && (
                      <span className="task-dates">{sub.start_date} ~ {sub.end_date}</span>
                    )}
                  </div>
                  {!isLocked && (
                    <>
                      <button
                        className="edit-task-btn"
                        onClick={(e) => { e.stopPropagation(); handleEditWorkItem(sub); }}
                        title="編輯子項"
                      >
                        ✏️
                      </button>
                      <button
                        className="hide-task-btn"
                        onClick={(e) => { e.stopPropagation(); toggleTicketHidden(subKey); }}
                        title="從報告中隱藏此子項"
                      >
                        隱藏
                      </button>
                    </>
                  )}
                </div>
                {expandedTickets.has(subKey) && sub.description && (
                  <div className="task-details">
                    <div className="task-description-full">
                      <div className="description-label">子項說明：</div>
                      <div className="description-content">
                        <MarkdownViewer content={sub.description} linkTickets={false} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  // work_item 的 Owner:assignees 已依 assigned_at 升冪,[0] 即 Owner;只有 user_id,靠 companyUsers 解析
  const resolveWorkItemOwner = (wi) => {
    const ownerA = wi.assignees?.[0];
    if (!ownerA) return null;
    const user = companyUsers.find(u => u.id === ownerA.user_id) || null;
    return { avatarUrl: user?.avatar_url, name: user?.name || '未知' };
  };

  // === 統計 ===
  const selectedMemberData = members.find(m => m.id === selectedMember);
  const totalTickets = memberProjects.reduce((sum, p) => sum + p.tickets.length, 0);
  const completedTickets = memberProjects.reduce(
    (sum, p) => sum + p.tickets.filter(t => t.status === 'completed' || t.status === 'closed').length,
    0
  );
  // 統計:子項是實際工作單位所以要算;_context 母項只是脈絡標題,不算。
  const flattenWorkItems = (list) => (list || []).flatMap(w => [w, ...(w.children || [])]).filter(w => !w._context);
  // ── 時數加總 ──
  // 剩餘的定義刻意不綁 status(否則得逐一討論哪些 status 算完成),只看兩件事:
  //   1. is_completed —— 就是畫面上那個勾選框,勾了剩餘一律是 0
  //   2. 預估 − 實際 —— WIP 自然被扣掉(估 6h、已投入 2h → 剩 4h)
  // 超支不給負數。沒人更新「實際」時 WIP 會被當成全額預估,那是保守估計,
  // 不會低估剩餘工作量。
  const sumHours = (list) => (list || []).reduce((acc, w) => {
    const est = Number(w.estimated_hours) || 0;
    const act = Number(w.actual_hours) || 0;
    return {
      estimated: acc.estimated + est,
      actual: acc.actual + act,
      remaining: acc.remaining + (w.is_completed ? 0 : Math.max(0, est - act)),
    };
  }, { estimated: 0, actual: 0, remaining: 0 });
  // 小數點只在需要時出現(7 而不是 7.0;7.5 保留)
  const fmtHours = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  const hoursSummary = (list) => {
    const { estimated, actual, remaining } = sumHours(list);
    if (!estimated && !actual) return null;
    return `預估 ${fmtHours(estimated)}h / 實際 ${fmtHours(actual)}h / 剩餘 ${fmtHours(remaining)}h`;
  };

  const totalWorkItems = memberProjects.reduce((sum, p) => sum + flattenWorkItems(p.workItems).length, 0);
  const completedWorkItems = memberProjects.reduce(
    (sum, p) => sum + flattenWorkItems(p.workItems).filter(w => w.status === 'completed' || w.status === 'closed').length,
    0
  );

  // Tab 過濾 + 隱藏 ticket
  const filteredProjects = useMemo(() => {
    let projects;
    if (activeTab === 'deliverables') return [];
    if (activeTab === 'all') projects = memberProjects;
    else projects = memberProjects.filter(p => p.id === activeTab);

    if (hiddenTicketIds.size === 0) return projects;
    return projects.map(p => ({
      ...p,
      tickets: p.tickets.filter(t => !hiddenTicketIds.has(t.id)),
      // 母項被隱藏 → 整組(含子項)一起消失;母項留著 → 只濾掉個別被隱藏的子項
      workItems: (p.workItems || [])
        .filter(w => !hiddenTicketIds.has(`wi-${w.id}`))
        .map(w => ({ ...w, children: (w.children || []).filter(c => !hiddenTicketIds.has(`wi-${c.id}`)) })),
    })).filter(p => p.tickets.length > 0 || p.workItems.length > 0);
  }, [activeTab, memberProjects, hiddenTicketIds]);

  // 所有 tab 都可能有交付項目（專案 tab 顯示該專案的，全部/交付項目顯示全部）
  // 交付項目(Phase 體系)引退中 → 先隱藏不顯示於 DailyReport;code 全保留,改回 true 即恢復
  const showDeliverables = false;

  // 依 tab 過濾交付項目：專案 tab 只顯示該專案的，全部/交付項目 tab 顯示全部
  const filteredDeliverableItems = useMemo(() => {
    if (activeTab === 'all' || activeTab === 'deliverables') return memberDeliverableItems;
    // 特定專案 tab → 只顯示該專案的交付項目
    return memberDeliverableItems.filter(item => {
      const phase = item.deliverables?.phases || {};
      return phase.project_id === activeTab;
    });
  }, [activeTab, memberDeliverableItems]);

  // 每次渲染前重置全域計數器
  globalMatchCounterRef.current = 0;

  const showDeptSelector = isCompanyAdmin() || isSuperAdmin();
  const canManageMembers = ['super_admin', 'company_admin', 'department_admin'].includes(profile?.role);

  return (
    <div className="daily-report-modal-overlay" onClick={handleClose}>
      <div className="daily-report-modal" onClick={(e) => e.stopPropagation()}>
        {/* 右上角工具列 */}
        <div className="modal-top-toolbar">
          {/* 管理顯示成員按鈕（管理員可見） */}
          {canManageMembers && (
            <button
              className="member-visibility-btn"
              onClick={() => setShowMemberVisibility(true)}
              title="管理顯示成員"
            >
              <span className="btn-text">管理顯示成員</span>
            </button>
          )}

          {/* 簡報模式按鈕 */}
          <button
            className={`broadcast-toggle-btn ${isInSession ? 'active' : ''}`}
            onClick={() => setShowBroadcastPanel(prev => !prev)}
            title={isInSession ? `簡報中: ${sessionCode}` : '簡報模式'}
          >
            <span className="btn-icon">📡</span>
            <span className="btn-text">{isInSession ? sessionCode : '簡報'}</span>
          </button>

          {/* 搜尋高亮輸入框 */}
          <div className="search-highlight-container">
            <div className="search-highlight-box">
              <input
                type="text"
                placeholder="輸入關鍵字高亮顯示..."
                value={highlightKeyword}
                onChange={(e) => setHighlightKeyword(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="search-highlight-input"
              />
              {highlightKeyword && (
                <>
                  <button
                    className="clear-search-btn"
                    onClick={() => setHighlightKeyword('')}
                    title="清除搜尋"
                  >
                    ✕
                  </button>
                  {totalMatches > 0 && (
                    <div className="search-navigation">
                      <button className="nav-btn" onClick={goToPreviousMatch} title="上一個 (Shift+Enter)">
                        ▲
                      </button>
                      <button className="nav-btn" onClick={goToNextMatch} title="下一個 (Enter)">
                        ▼
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
            {highlightKeyword && totalMatches > 0 && (
              <div className="match-count-hint">
                第 {currentMatchIndex + 1} / {totalMatches} 個結果
              </div>
            )}
            {highlightKeyword && totalMatches === 0 && (
              <div className="match-count-hint no-results">找不到符合的結果</div>
            )}
          </div>

          {/* 重新整理按鈕 */}
          <button
            className="modal-refresh-btn"
            onClick={handleRefresh}
            disabled={!selectedMember || loading}
            title="重新載入資料"
          >
            ↻
          </button>

          {/* 關閉按鈕 */}
          <button className="modal-close-btn" onClick={handleClose} title="關閉">
            ✕
          </button>
        </div>

        <div className="daily-report-view">
          <div className="daily-report-header">
            <div className="header-title">
              <h2>
                {new Date().toLocaleDateString('zh-TW', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                }).replace(/\//g, '/')}{' '}
                Daily Report
              </h2>
            </div>

            {/* 部門選擇器（公司管理員+） */}
            {showDeptSelector && (
              <div className="department-selector">
                <label className="dept-label">部門：</label>
                {departments.length > 0 ? (
                  <CustomSelect
                    value={selectedDepartmentId || ''}
                    onChange={(val) => { setSelectedDepartmentId(val); saveDepartmentSelection(val); }}
                    options={departments.map(dept => ({ value: dept.id, label: dept.name }))}
                    placeholder="選擇部門..."
                    searchable={departments.length > 5}
                    className="dept-custom-select"
                  />
                ) : (
                  <span className="dept-loading">載入中...</span>
                )}
              </div>
            )}
          </div>

          {/* 簡報模式面板 */}
          {showBroadcastPanel && (
            <div className="broadcast-panel">
              {!isInSession ? (
                <div className="broadcast-actions">
                  {departmentCode && (
                    <div className="dept-code-hint">
                      <span className="dept-code-label">部門代碼：</span>
                      <span className="dept-code-value">{departmentCode}</span>
                    </div>
                  )}
                  <button
                    className="broadcast-create-btn"
                    onClick={() => { handleCreateSession(departmentCode || undefined); }}
                  >
                    建立簡報 (Host)
                  </button>
                  <div className="broadcast-join">
                    <input
                      type="text"
                      className="broadcast-join-input"
                      placeholder="輸入 6 碼..."
                      value={joinCode}
                      onChange={(e) => setJoinCode(e.target.value.toUpperCase().slice(0, 6))}
                      onKeyDown={(e) => {
                        if (e.nativeEvent.isComposing) return;
                        if (e.key === 'Enter' && joinCode.length === 6) handleJoinSession(joinCode);
                      }}
                      maxLength={6}
                    />
                    <button
                      className="broadcast-join-btn"
                      onClick={() => handleJoinSession(joinCode)}
                      disabled={joinCode.length !== 6}
                    >
                      加入
                    </button>
                  </div>
                </div>
              ) : (
                <div className="broadcast-session-info">
                  <div className="session-code-row">
                    <span className="session-label">Session:</span>
                    <span className="session-code">{sessionCode}</span>
                    <button
                      className="copy-code-btn"
                      onClick={() => { navigator.clipboard.writeText(sessionCode); toast.success('已複製'); }}
                      title="複製 Code"
                    >
                      📋
                    </button>
                  </div>

                  <div className="session-participants">
                    <span className="participants-label">參與者：</span>
                    {participants.map(p => (
                      <button
                        key={p.id}
                        className={`participant-chip ${p.id === presenterId ? 'is-presenter' : ''} ${isHost ? 'clickable' : ''}`}
                        onClick={() => isHost && assignPresenter(p.id)}
                        title={isHost ? `指定 ${p.name} 為報告者` : (p.id === presenterId ? '報告者' : '')}
                      >
                        <Avatar avatarUrl={p.avatar_url} name={p.name} size="tiny" />
                        <span className="participant-name">{p.name}</span>
                        {p.id === presenterId && <span className="presenter-badge">報告者</span>}
                        {p.isHost && <span className="host-badge">Host</span>}
                      </button>
                    ))}
                  </div>

                  <div className="session-actions">
                    {isHost ? (
                      <button className="session-end-btn" onClick={handleEndSession}>結束簡報</button>
                    ) : (
                      <button className="session-leave-btn" onClick={handleLeaveSession}>離開簡報</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 簡報進行中指示 */}
          {isInSession && !isPresenter && presenterId && (
            <div className="presenter-indicator-bar">
              <span>📡 {participants.find(p => p.id === presenterId)?.name || '...'} 報告中</span>
            </div>
          )}

          {/* 成員選擇器（Host 被鎖定時隱藏，避免與上方切換報告者混淆） */}
          {!isLocked && (
          <div className="member-selector">
            <div className="member-list">
              {membersLoading ? (
                <div className="member-skeleton-row">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="member-skeleton">
                      <div className="skeleton skeleton-circle" />
                      <div className="skeleton skeleton-text" />
                    </div>
                  ))}
                </div>
              ) : members.length === 0 ? (
                <div className="no-members-hint">
                  {canManageMembers
                    ? '尚未設定顯示成員，請點擊「管理顯示成員」新增'
                    : '管理員尚未設定此部門的 Daily Report 成員'}
                </div>
              ) : (
                members.map(member => (
                  <div key={member.id} className="member-card-wrapper">
                    <button
                      className={`member-btn ${selectedMember === member.id ? 'active' : ''}`}
                      onClick={() => !isLocked && setSelectedMember(member.id)}
                      style={isLocked ? { pointerEvents: 'none', opacity: 0.7 } : undefined}
                    >
                      <Avatar
                        avatarUrl={member.avatar_url}
                        name={member.name}
                        size="small"
                        className="member-avatar"
                      />
                      <span className="member-name">{member.name || member.email || '未命名'}</span>
                    </button>
                    {member.id === profile?.id && (
                      <button
                        className="member-settings-btn"
                        onClick={() => setShowProjectSelector(true)}
                        title="設定我在 Daily Report 中展示的專案"
                      >
                        ⚙️
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
          )}

          {/* 成員專案和 Tickets */}
          {selectedMember && (
            <div className="member-projects" ref={scrollContainerRef} onScroll={handlePresenterScroll}>
              {loading ? (
                <div className="loading-state">
                  <p>載入中...</p>
                </div>
              ) : (memberProjects.length === 0 && memberDeliverableItems.length === 0) ? (
                <div className="empty-state">
                  <h3>目前無負責的工作項目</h3>
                  <p>{selectedMemberData?.name || '此成員'} 目前沒有負責任何 Ticket 或工作項目</p>
                </div>
              ) : (
                <>
                  {memberProjects.length > 0 && (
                  <div className="projects-summary">
                    <h3>
                      {selectedMemberData?.name || '成員'} 的工作概況
                    </h3>
                    <div className="summary-stats">
                      <span className="stat-item">
                        <span className="stat-label">專案數量：</span>
                        <span className="stat-value">{memberProjects.length}</span>
                      </span>
                      <span className="stat-item">
                        <span className="stat-label">Ticket 總數：</span>
                        <span className="stat-value">
                          {totalTickets}（已完成 {completedTickets}）
                        </span>
                      </span>
                      <span className="stat-item">
                        <span className="stat-label">工作項目：</span>
                        <span className="stat-value">
                          {totalWorkItems}（已完成 {completedWorkItems}）
                        </span>
                      </span>
                    </div>
                  </div>
                  )}

                  {/* Project Tabs */}
                  <div className="project-tabs">
                    <button
                      className={`project-tab ${activeTab === 'all' ? 'active' : ''}`}
                      onClick={() => !isLocked && setActiveTab('all')}
                    >
                      全部
                      <span className="tab-badge">{totalTickets + totalWorkItems}</span>
                    </button>
                    {memberProjects.map(project => (
                      <button
                        key={project.id}
                        className={`project-tab ${activeTab === project.id ? 'active' : ''}`}
                        onClick={() => !isLocked && setActiveTab(project.id)}
                        style={{ '--tab-color': project.color || '#667eea' }}
                      >
                        <span className="tab-icon">{project.icon || '📊'}</span>
                        <span className="tab-name">{project.name}</span>
                        <span className="tab-badge">{project.tickets.length + flattenWorkItems(project.workItems).length}</span>
                      </button>
                    ))}
                    {showDeliverables && memberDeliverableItems.length > 0 && (
                      <button
                        className={`project-tab deliverables-tab ${activeTab === 'deliverables' ? 'active' : ''}`}
                        onClick={() => !isLocked && setActiveTab('deliverables')}
                      >
                        交付項目
                        <span className="tab-badge">{memberDeliverableItems.length}</span>
                      </button>
                    )}
                  </div>

                  {/* 隱藏 Ticket 提示列 */}
                  {hiddenTicketIds.size > 0 && (
                    <div className="hidden-tickets-bar">
                      <div className="hidden-bar-summary" onClick={() => setShowHiddenList(prev => !prev)}>
                        <span className="hidden-count">已隱藏 {hiddenTicketIds.size} 個項目</span>
                        <span className="hidden-expand-icon">{showHiddenList ? '▲' : '▼'}</span>
                        <button
                          className="restore-all-btn"
                          onClick={(e) => { e.stopPropagation(); setHiddenTicketIds(new Set()); setShowHiddenList(false); }}
                        >
                          全部恢復
                        </button>
                      </div>
                      {showHiddenList && (
                        <div className="hidden-tickets-list">
                          {memberProjects.flatMap(p => [
                            ...p.tickets
                              .filter(t => hiddenTicketIds.has(t.id))
                              .map(t => ({ hideKey: t.id, no: `#${t.ticket_no}`, title: t.title, projectName: p.name, projectIcon: p.icon })),
                            ...flattenWorkItems(p.workItems)
                              .filter(w => hiddenTicketIds.has(`wi-${w.id}`))
                              .map(w => ({ hideKey: `wi-${w.id}`, no: w.work_item_no ? `工作項目 #${w.work_item_no}` : '子項', title: w.title, projectName: p.name, projectIcon: p.icon })),
                          ]).map(item => (
                            <div key={item.hideKey} className="hidden-ticket-item">
                              <span className="hidden-ticket-project">{item.projectIcon || '📊'} {item.projectName}</span>
                              <span className="hidden-ticket-title">{item.no} {item.title}</span>
                              <button
                                className="restore-btn"
                                onClick={() => toggleTicketHidden(item.hideKey)}
                              >
                                恢復
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {filteredProjects.length > 0 && (
                  <div className="projects-list">
                    {filteredProjects.map(project => {
                      const projectCompletedCount = project.tickets.filter(
                        t => t.status === 'completed' || t.status === 'closed'
                      ).length;
                      const projectWorkItems = flattenWorkItems(project.workItems);
                      const projectWiCompletedCount = projectWorkItems.filter(
                        w => w.status === 'completed' || w.status === 'closed'
                      ).length;

                      return (
                        <div key={project.id} className="project-section">
                          <div
                            className="project-header"
                            style={{ borderLeftColor: project.color }}
                            onClick={() => toggleProjectExpand(project.id)}
                          >
                            <div className="project-header-main">
                              <span className="expand-icon">
                                {expandedProjects.has(project.id) ? '▼' : '▶'}
                              </span>
                              <span className="project-icon">{project.icon || '📊'}</span>
                              {project.externalUrl ? (
                                <div className="project-name">
                                  <a
                                    href={project.externalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="project-link"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {highlightText(project.name, highlightKeyword)}
                                  </a>
                                </div>
                              ) : (
                                <h4 className="project-name">
                                  {highlightText(project.name, highlightKeyword)}
                                </h4>
                              )}
                              <span className="task-count">
                                {project.tickets.length} 個 Ticket
                                {projectWorkItems.length > 0 && ` ＋ ${projectWorkItems.length} 個工作項目`}
                                （已完成 {projectCompletedCount + projectWiCompletedCount} 個）
                                {hoursSummary(projectWorkItems) && (
                                  <span className="task-count-hours">・{hoursSummary(projectWorkItems)}</span>
                                )}
                              </span>
                            </div>
                            {project.description && (
                              <div className="project-description">
                                {highlightText(project.description, highlightKeyword)}
                              </div>
                            )}
                          </div>

                          {expandedProjects.has(project.id) && (
                            <div className="tasks-list">
                              {project.tickets.map(ticket => {
                                const owner = resolveTicketOwner(ticket);
                                return (
                                <div
                                  key={ticket.id}
                                  className={`task-item ${expandedTickets.has(ticket.id) ? 'expanded' : ''}`}
                                >
                                  <div
                                    className="task-header"
                                    onClick={() => toggleTicketExpand(ticket.id)}
                                  >
                                    <span className="expand-icon">
                                      {ticket.description
                                        ? (expandedTickets.has(ticket.id) ? '▼' : '▶')
                                        : ''}
                                    </span>

                                    <div className="task-main">
                                      <div
                                        className="task-status-indicator"
                                        style={{ background: getStatusColor(ticket.status) }}
                                      />
                                      <div className="task-info">
                                        <h5 className="task-title">
                                          <span className="ticket-no-badge">#{ticket.ticket_no}</span>
                                          {highlightText(ticket.title, highlightKeyword)}
                                        </h5>
                                      </div>
                                    </div>

                                    <div className="task-meta">
                                      {owner && (
                                        <span className="task-owner" title={`Owner：${owner.name}`}>
                                          <Avatar
                                            avatarUrl={owner.avatarUrl}
                                            name={owner.name}
                                            size="xsmall"
                                          />
                                          <span className="task-owner-name">{owner.name}</span>
                                        </span>
                                      )}
                                      <span
                                        className="task-status"
                                        style={{
                                          borderColor: getStatusColor(ticket.status),
                                          color: getStatusColor(ticket.status),
                                        }}
                                      >
                                        {getStatusLabel(ticket.status)}
                                      </span>
                                      {ticket.start_date && ticket.end_date && (
                                        <span className="task-dates">
                                          {ticket.start_date} ~ {ticket.end_date}
                                        </span>
                                      )}
                                    </div>

                                    {!isLocked && (
                                      <>
                                        <button
                                          className="edit-task-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleEditTicket(ticket);
                                          }}
                                          title="編輯 Ticket"
                                        >
                                          ✏️
                                        </button>
                                        <button
                                          className="hide-task-btn"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTicketHidden(ticket.id);
                                          }}
                                          title="從報告中隱藏此 Ticket"
                                        >
                                          隱藏
                                        </button>
                                      </>
                                    )}
                                  </div>

                                  {expandedTickets.has(ticket.id) && ticket.description && (
                                    <div className="task-details">
                                      <div className="task-description-full">
                                        <div className="description-label">Ticket 說明：</div>
                                        <div className="description-content">
                                          <MarkdownViewer content={ticket.description} />
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                );
                              })}
                              {/* work_item(工作項目)混排在同專案 ticket 之後;子項收在母項底下。
                                  群組用「分隔列」呈現而不是再嵌一層容器:日報是唸稿用的,需要的是同群組
                                  聚在一起 + 講得出群組名,不是可收合的樹。不加縮排,層級仍是三層,
                                  子項那層才不會被推到很深。Ticket 沒有群組概念,所以排在分隔列之前。*/}
                              {(() => {
                                const roots = project.workItems || [];
                                const groupKeys = [...new Set(roots.map(groupKeyOf))];
                                // 只有「全部未分組」時分隔列是純噪音 → 不渲染。
                                // 單一具名群組仍要顯示(「這些都屬於 規格 v1」是有用資訊)。
                                const showGroupHeaders = !(groupKeys.length <= 1 && groupKeys[0] === UNGROUPED_KEY);
                                const groupItems = new Map();
                                roots.forEach(w => {
                                  const k = groupKeyOf(w);
                                  if (!groupItems.has(k)) groupItems.set(k, []);
                                  groupItems.get(k).push(w, ...(w.children || []));
                                });
                                let lastKey = null;
                                return roots.map(wi => {
                                  const gKey = groupKeyOf(wi);
                                  const needHeader = showGroupHeaders && gKey !== lastKey;
                                  lastKey = gKey;
                                  const gItems = (groupItems.get(gKey) || []).filter(w => !w._context);
                                  const gHours = hoursSummary(gItems);
                                  return (
                                    <React.Fragment key={`g-${gKey}-${wi.id}`}>
                                      {needHeader && (
                                        <div className="wi-group-divider">
                                          <span className="wi-group-divider-name">{groupNameOf(wi)}</span>
                                          <span className="wi-group-divider-count">{gItems.length} 項</span>
                                          {gHours && <span className="wi-group-divider-hours">{gHours}</span>}
                                        </div>
                                      )}
                                      {renderWorkItemRow(wi)}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </>
              )}
              {/* 交付項目區塊 */}
              {!loading && showDeliverables && filteredDeliverableItems.length > 0 && (
            <div className="deliverable-items-section">
              <h3 className="di-section-title">交付項目</h3>
              {(() => {
                // 按 Project > Phase > Deliverable 分群
                const grouped = {};
                filteredDeliverableItems.forEach((item) => {
                  const deliverable = item.deliverables || {};
                  const phase = deliverable.phases || {};
                  const project = phase.projects || {};
                  const key = `${phase.project_id}-${phase.id}-${deliverable.id}`;
                  if (!grouped[key]) {
                    grouped[key] = {
                      projectName: project.name || '未知專案',
                      projectColor: project.color || '#667eea',
                      phaseName: phase.name || '未知階段',
                      deliverableTitle: deliverable.title || '未知頁面',
                      items: [],
                    };
                  }
                  grouped[key].items.push(item);
                });

                return Object.entries(grouped).map(([key, group]) => (
                  <div key={key} className="di-group">
                    <div className="di-group-header">
                      <span className="di-dot" style={{ backgroundColor: group.projectColor }} />
                      <span className="di-project">{group.projectName}</span>
                      <span className="di-sep">&gt;</span>
                      <span className="di-phase">{group.phaseName}</span>
                      <span className="di-sep">&gt;</span>
                      <span className="di-deliverable">{group.deliverableTitle}</span>
                    </div>
                    {group.items.map((item) => (
                      <div key={item.id} className={`di-item ${item.is_completed ? 'completed' : ''}`}>
                        <span className={`di-check ${item.is_completed ? 'done' : ''}`}>
                          {item.is_completed ? '✓' : '○'}
                        </span>
                        <span className="di-role">{item.role_type}</span>
                        <span className="di-title">{item.title}</span>
                        {item.estimated_hours > 0 && (
                          <span className="di-hours">{item.estimated_hours}h</span>
                        )}
                        {item.actual_hours > 0 && (
                          <span className="di-hours di-actual-hours">實際 {item.actual_hours}h</span>
                        )}
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}
            </div>
          )}

          {/* Project Selector Modal */}
          <BetaDailyReportProjectSelector
            isOpen={showProjectSelector}
            onClose={() => setShowProjectSelector(false)}
            onSuccess={handleRefresh}
          />

          {/* Member Visibility Modal */}
          <MemberVisibilityModal
            isOpen={showMemberVisibility}
            onClose={() => setShowMemberVisibility(false)}
            departmentId={effectiveDepartmentId}
            onSuccess={() => {
              loadMembers(effectiveDepartmentId);
              setSelectedMember(null);
              setMemberProjects([]);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BetaDailyReportView;
